// Alta de un pedido de venta desde el archivo del cliente: las reglas.
//
// Todo lo que decide algo vive aquí para que los cuatro pasos del asistente sean
// componentes de pintar, y para que las reglas —la herencia de la cabecera, las
// referencias repetidas, los avisos— se puedan probar sin montar el cajón.
//
// LA DECISIÓN DE FONDO: una línea guarda `almacen: ''` o `fecha: ''` para decir
// «lo que diga la cabecera». No se copia el valor de la cabecera a cada línea.
//
// Parece un detalle y es lo que hace que funcione lo que pidió el usuario: al
// cambiar la cabecera, todas las líneas que heredan la siguen solas, y las que
// él tocó a mano se quedan como las dejó. Si se copiara el valor, no habría
// forma de distinguir «esta línea vale VID porque lo heredó» de «esta línea vale
// VID porque yo lo elegí», y cualquier cambio de cabecera pisaría el trabajo del
// usuario sin avisar.
import type {
  CabeceraBorrador,
  LecturaArchivo,
  LineaBorrador,
  LineaRecuperable,
  PedidoArchivoRequest,
  ProductoLeido,
} from '@/types'

/**
 * Tope de la referencia del cliente.
 *
 * Lo impone D365: `SalesTable.CustomerRef` es un `CustVendRef` de 20
 * caracteres y el robot la teclea tal cual. Pasarse no falla aquí — falla, o
 * peor, se RECORTA en silencio, al otro lado y con el pedido a medio crear.
 *
 * El mismo número vive en `backend/app/schemas/pedidos_archivo.py`
 * (`MAX_REF_CLIENTE`), que lo valida otra vez por si alguien llama a la API sin
 * pasar por esta pantalla. Los dos tienen que decir lo mismo.
 */
export const MAX_REF_CLIENTE = 20

/** Identidad local de una fila. No es el índice: el usuario quita líneas y añade
 *  recuperables, y un índice movería de sitio las marcas y los avisos. */
let contador = 0
export function nuevoId(prefijo = 'l'): string {
  contador += 1
  return `${prefijo}-${contador}`
}

// ── Del archivo al borrador ───────────────────────────────────────────────────

export function lineaDeProducto(p: ProductoLeido): LineaBorrador {
  return {
    id: nuevoId(),
    referencia: p.referencia,
    ean: p.ean ?? '',
    unidades: p.unidades,
    unidadesSinRedondear: p.unidadesSinRedondear ?? null,
    descripcion: p.descripcion ?? '',
    almacen: '',   // hereda
    fecha: '',     // hereda
    recuperada: false,
  }
}

/** Una línea que el cliente quitó en una solicitud anterior y que vuelve.
 *
 *  Entra SIN EAN —su grano no lo trae— y marcada, para que el usuario vea que
 *  esa línea depende sólo de la referencia. */
export function lineaDeRecuperable(r: LineaRecuperable): LineaBorrador {
  return {
    id: nuevoId('r'),
    referencia: r.referencia,
    ean: '',
    unidades: r.cantidad,
    unidadesSinRedondear: null,
    descripcion: '',
    almacen: '',
    fecha: '',
    recuperada: true,
    color: r.color || undefined,
    estilo: r.estilo || undefined,
  }
}

export function borradorDeLectura(lectura: LecturaArchivo): LineaBorrador[] {
  return lectura.productos.map(lineaDeProducto)
}

// ── Herencia de la cabecera ───────────────────────────────────────────────────

export function almacenEfectivo(linea: LineaBorrador, cab: CabeceraBorrador): string {
  return linea.almacen || cab.almacen
}

export function fechaEfectiva(linea: LineaBorrador, cab: CabeceraBorrador): string {
  return linea.fecha || cab.fecha
}

/** ¿Esta línea lleva algo puesto a mano? Es lo que la pantalla acentúa. */
export function tieneValorPropio(linea: LineaBorrador): boolean {
  return !!linea.almacen || !!linea.fecha
}

export function cuantasTocadas(lineas: LineaBorrador[]): number {
  return lineas.filter(tieneValorPropio).length
}

/** Devuelve todas las líneas a heredar de la cabecera.
 *
 *  Esto es lo que hace el botón «aplicar a todas», y por eso NO copia el valor
 *  de la cabecera: lo que hace es QUITAR los valores propios. El efecto que ve
 *  el usuario es el mismo —todo pasa a valer lo que dice la cabecera— pero
 *  además deja las líneas siguiendo los cambios que venga a hacer después. */
export function restablecerTodas(
  lineas: LineaBorrador[],
  campo?: 'almacen' | 'fecha',
): LineaBorrador[] {
  return lineas.map((l) => ({
    ...l,
    almacen: campo === 'fecha' ? l.almacen : '',
    fecha: campo === 'almacen' ? l.fecha : '',
  }))
}

// ── Avisos ────────────────────────────────────────────────────────────────────

/** Referencias que aparecen más de una vez.
 *
 *  NO se suman: el archivo del cliente puede traer la misma referencia dos veces
 *  a propósito —dos fechas, dos destinos—, y juntarlas escondería lo que mandó.
 *  Se marcan las DOS filas, no sólo la segunda: el usuario tiene que poder ver
 *  el par para decidir. */
export function referenciasRepetidas(lineas: LineaBorrador[]): Set<string> {
  const cuenta = new Map<string, number>()
  for (const l of lineas) {
    const clave = l.referencia.trim().toUpperCase()
    cuenta.set(clave, (cuenta.get(clave) ?? 0) + 1)
  }
  const repes = new Set<string>()
  for (const [clave, n] of cuenta) if (n > 1) repes.add(clave)
  return repes
}

export function esRepetida(linea: LineaBorrador, repes: Set<string>): boolean {
  return repes.has(linea.referencia.trim().toUpperCase())
}

/** ¿El flujo cambió la cantidad que pedía el cliente? */
export function fueRedondeada(linea: LineaBorrador): boolean {
  return (
    linea.unidadesSinRedondear != null &&
    linea.unidadesSinRedondear !== linea.unidades
  )
}

export interface ResumenAvisos {
  sinEan: number
  repetidas: number
  recuperadas: number
  conValorPropio: number
  redondeadas: number
  total: number
}

/** Los avisos recontados para la última pantalla.
 *
 *  Se cuenta por LÍNEA y una sola vez por motivo: un recuento que suma dos veces
 *  la misma fila convierte el aviso en ruido y deja de leerse. */
export function resumenAvisos(lineas: LineaBorrador[]): ResumenAvisos {
  const repes = referenciasRepetidas(lineas)
  const r: ResumenAvisos = {
    sinEan: 0, repetidas: 0, recuperadas: 0,
    conValorPropio: 0, redondeadas: 0, total: 0,
  }
  for (const l of lineas) {
    if (!l.ean) r.sinEan += 1
    if (esRepetida(l, repes)) r.repetidas += 1
    if (l.recuperada) r.recuperadas += 1
    if (tieneValorPropio(l)) r.conValorPropio += 1
    if (fueRedondeada(l)) r.redondeadas += 1
  }
  r.total = r.sinEan + r.repetidas + r.recuperadas + r.redondeadas
  return r
}

// ── Totales ───────────────────────────────────────────────────────────────────

export function totales(lineas: LineaBorrador[]): { lineas: number; unidades: number } {
  return {
    lineas: lineas.length,
    unidades: lineas.reduce((a, l) => a + (Number(l.unidades) || 0), 0),
  }
}

// ── Validación ────────────────────────────────────────────────────────────────

/** Qué falta para poder cursar. Códigos, no texto: la pantalla es bilingüe. */
export function validarCabecera(cab: CabeceraBorrador): string[] {
  const faltan: string[] = []
  if (!cab.empresa) faltan.push('falta_empresa')
  if (!cab.grupo) faltan.push('falta_grupo')
  if (!cab.clienteId) faltan.push('falta_cliente')
  if (!cab.direccionId) faltan.push('falta_direccion')
  if (!cab.fecha) faltan.push('falta_fecha')
  if (!cab.almacen) faltan.push('falta_almacen')
  // Se avisa y se para, NO se recorta: recortar la referencia del cliente le
  // cambia el dato sin decírselo, y es el campo por el que ese cliente va a
  // preguntar por su pedido.
  if (cab.numeroPedidoCliente.trim().length > MAX_REF_CLIENTE) {
    faltan.push('referencia_cliente_larga')
  }
  // Un correo mal escrito no rompe nada aquí: lo descubre quien no recibe la
  // copia, que es justo quien no puede quejarse.
  if (cab.copia.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cab.copia.trim())) {
    faltan.push('copia_no_es_un_correo')
  }
  return faltan
}

export function validarLineas(lineas: LineaBorrador[]): string[] {
  const faltan: string[] = []
  if (lineas.length === 0) faltan.push('sin_lineas')
  if (lineas.some((l) => !l.referencia.trim())) faltan.push('linea_sin_referencia')
  if (lineas.some((l) => !(Number(l.unidades) > 0))) faltan.push('linea_sin_unidades')
  return faltan
}

export function puedeCursar(cab: CabeceraBorrador, lineas: LineaBorrador[]): boolean {
  return validarCabecera(cab).length === 0 && validarLineas(lineas).length === 0
}

// ── Lo que se manda ───────────────────────────────────────────────────────────

/** Arma la solicitud resolviendo la herencia.
 *
 *  Las líneas viajan con su almacén y su fecha EXPLÍCITOS aunque los hereden: lo
 *  que se manda tiene que ser exactamente lo que el usuario ha visto en la
 *  tabla, sin que nadie al otro lado tenga que volver a deducirlo. */
export function construirSolicitud(
  cab: CabeceraBorrador,
  lineas: LineaBorrador[],
  archivos: { nombre: string; bytes: number }[],
  /** «Ya sé que se parece a algo que cursé antes; mándalo igual». Lo pone el
   *  usuario contestando al aviso, nunca la pantalla por su cuenta. */
  confirmarDuplicado = false,
): PedidoArchivoRequest {
  return {
    empresa: cab.empresa,
    custGroup: cab.grupo,
    almacen: cab.almacen,
    clienteId: cab.clienteId,
    cliente: cab.clienteNombre,
    customerOrderNumber: cab.numeroPedidoCliente,
    direccionEntregaId: cab.direccionId,
    direccionEntrega: cab.direccionTexto,
    fechaCabecera: cab.fecha,
    // Tal y como lo leyó el flujo 1. No se toca ni se normaliza: lo que viaja
    // es lo que se le enseñó al usuario.
    tipoPago: cab.tipoPago,
    nota: cab.nota.trim(),
    copia: cab.copia.trim(),
    confirmarDuplicado,
    archivos,
    lineas: lineas.map((l) => ({
      referencia: l.referencia.trim(),
      ean: l.ean,
      unidades: Number(l.unidades),
      unidadesSinRedondear: l.unidadesSinRedondear ?? null,
      almacenLinea: almacenEfectivo(l, cab),
      fechaEnvio: fechaEfectiva(l, cab),
    })),
  }
}
